import { useEffect, useRef } from 'react'
import './code-editor.css'

/**
 * The email body editor (PRN-048): bold, italic, underline, numbered and
 * bulleted lists, and a link — what the reference's editor offers, and what a
 * supplier's mail client shows reliably.
 *
 * A contentEditable area with the browser's own formatting commands, rather
 * than an editor library: the toolbar is six buttons, and placeholders such
 * as {doc_no} are plain text that the editor has no reason to touch. The
 * server keeps only formatting tags and safe links, whatever is pasted in.
 */
export interface RichTextEditorProps {
  readonly value: string
  readonly onChange: (html: string) => void
  readonly readOnly?: boolean
  readonly label?: string
  /** Offered in a picker that inserts `{name}` at the caret. */
  readonly placeholders?: readonly string[]
}

const COMMANDS: ReadonlyArray<{ cmd: string; label: string; title: string }> = [
  { cmd: 'bold', label: 'B', title: 'Bold' },
  { cmd: 'italic', label: 'I', title: 'Italic' },
  { cmd: 'underline', label: 'U', title: 'Underline' },
  { cmd: 'insertOrderedList', label: '1.', title: 'Numbered list' },
  { cmd: 'insertUnorderedList', label: '•', title: 'Bullet list' },
]

export const RichTextEditor = ({ value, onChange, readOnly = false, label = 'Email body', placeholders = [] }: RichTextEditorProps) => {
  const area = useRef<HTMLDivElement>(null)
  const last = useRef<string>('')

  // Written into the DOM only when it changed from outside — rewriting it on
  // every keystroke would throw the caret back to the start.
  useEffect(() => {
    if (area.current && value !== last.current) {
      area.current.innerHTML = value
      last.current = value
    }
  }, [value])

  const emit = (): void => {
    const html = area.current?.innerHTML ?? ''
    last.current = html
    onChange(html)
  }

  const run = (cmd: string, arg?: string): void => {
    area.current?.focus()
    document.execCommand(cmd, false, arg)
    emit()
  }

  return (
    <div className="rich-editor">
      {!readOnly ? (
        <div className="rich-editor__bar" role="toolbar" aria-label="Formatting">
          {COMMANDS.map((c) => (
            <button key={c.cmd} type="button" title={c.title} aria-label={c.title} onMouseDown={(e) => e.preventDefault()} onClick={() => run(c.cmd)}>
              {c.label}
            </button>
          ))}
          <button type="button" title="Link" aria-label="Link" onMouseDown={(e) => e.preventDefault()}
            onClick={() => {
              const url = window.prompt('Link to (https://…)')
              if (url && /^(https?:|mailto:)/i.test(url.trim())) run('createLink', url.trim())
            }}>
            Link
          </button>
          {placeholders.length > 0 ? (
            <select
              className="control select"
              aria-label="Insert a placeholder"
              style={{ maxWidth: 240, marginLeft: 'auto' }}
              value=""
              onChange={(e) => { if (e.currentTarget.value) run('insertText', `{${e.currentTarget.value}}`) }}
            >
              <option value="">Insert a placeholder…</option>
              {placeholders.map((p) => <option key={p} value={p}>{`{${p}}`}</option>)}
            </select>
          ) : null}
        </div>
      ) : null}
      <div
        ref={area}
        className="rich-editor__area"
        role="textbox"
        aria-multiline="true"
        aria-label={label}
        contentEditable={!readOnly}
        suppressContentEditableWarning
        onInput={emit}
        onBlur={emit}
      />
    </div>
  )
}
